import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import { append, remove } from 'ramda'
import React from 'react'
import { v1 as uuid } from 'uuid'

import ConditionGroup from '@/app/component/conditions/condition-group'
import { operands } from '@/app/component/conditions/definitions'
import type { Condition, PersonalizationType } from '@/app/types'

type Props = {
  error?: boolean
  groups: Condition[]
  personalizationList: PersonalizationType
  onChange: (groups: Condition[]) => void
}

const QuotaConditionEditor: React.FC<Props> = ({ error = false, groups, personalizationList, onChange }) => {
  const [groupIds, setGroupIds] = React.useState<string[]>(() => groups.map(() => uuid()))

  if (!groups.length) {
    return (
      <div>
        <Typography color="text.secondary" sx={{ mb: 1.5 }} variant="body2">
          Every response counts toward this quota when the “Mark Survey as Completed” action runs.
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={() => {
            setGroupIds([uuid()])
            onChange([['and', ['', '', '']] as Condition])
          }}
        >
          Add condition
        </Button>
      </div>
    )
  }

  return (
    <div>
      {groups.map((conditionRow, rowIndex) => (
        <div key={groupIds[rowIndex]}>
          <Typography sx={{ mb: 1 }} variant="subtitle2">
            {rowIndex === 0 ? (
              'A response counts toward this quota when it matches:'
            ) : (
              <>
                <Typography color="primary.main" component="strong">
                  OR
                </Typography>{' '}
                when it matches:
              </>
            )}
          </Typography>
          <ConditionGroup
            conditionRow={conditionRow as Condition[]}
            error={error}
            groupedOperators
            groups={{}}
            isFirst={rowIndex === 0}
            isLast={rowIndex === groups.length - 1}
            operands={operands}
            personalizationList={personalizationList}
            rowIndex={rowIndex}
            segments={{}}
            validateOnBlur
            onAddConditionGroup={() => {
              setGroupIds((currentGroupIds) => append(uuid(), currentGroupIds))
              onChange(append(['and', ['', '', '']] as Condition, groups))
            }}
            onChangeCondition={({ updatedConditionGroup }) => {
              if (updatedConditionGroup.length === 1) {
                setGroupIds((currentGroupIds) => remove(rowIndex, 1, currentGroupIds))
                onChange(remove(rowIndex, 1, groups))
                return
              }

              const updatedGroups = [...groups]
              updatedGroups[rowIndex] = updatedConditionGroup
              onChange(updatedGroups)
            }}
          />
          {rowIndex < groups.length - 1 && <Divider sx={{ mb: 2 }} />}
        </div>
      ))}
    </div>
  )
}

export default QuotaConditionEditor
